import { useCallback, useEffect, useMemo, useState } from "react";
import { LogOut, Lock, Search, Mail, Phone, Building2, RefreshCw } from "lucide-react";
import api from "@/lib/api";
import Logo from "@/components/Logo";

const TOKEN_KEY = "axis_admin_token";

function formatError(detail) {
  if (!detail) return "Algo deu errado. Tente novamente.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || "").filter(Boolean).join(" ");
  return String(detail);
}

const authHeader = () => ({ Authorization: `Bearer ${sessionStorage.getItem(TOKEN_KEY)}` });

function LoginForm({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password }, { withCredentials: true });
      sessionStorage.setItem(TOKEN_KEY, data.token);
      onLogin(data);
    } catch (err) {
      setError(formatError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#050B18] px-6">
      <form onSubmit={submit} data-testid="admin-login-form" className="w-full max-w-sm bg-white p-8 space-y-5">
        <div className="flex items-center gap-3">
          <Lock className="w-5 h-5 text-[#1E3A8A]" />
          <h1 className="font-display text-2xl font-semibold tracking-tight">Painel de leads</h1>
        </div>
        <input
          data-testid="admin-login-email"
          type="email"
          required
          placeholder="E-mail"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full border border-gray-300 px-4 py-3 text-sm focus:border-[#3B82F6] outline-none"
        />
        <input
          data-testid="admin-login-password"
          type="password"
          required
          placeholder="Senha"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="w-full border border-gray-300 px-4 py-3 text-sm focus:border-[#3B82F6] outline-none"
        />
        {error && (
          <p data-testid="admin-login-error" className="text-sm text-[#DC2626]">{error}</p>
        )}
        <button
          type="submit"
          disabled={loading}
          data-testid="admin-login-submit"
          className="w-full bg-[#1E3A8A] text-white py-3 text-sm font-medium hover:bg-[#172554] transition-colors disabled:opacity-60"
        >
          {loading ? "Entrando..." : "Entrar"}
        </button>
      </form>
    </div>
  );
}

function LeadCard({ lead }) {
  return (
    <article data-testid={`lead-${lead.id}`} className="bg-white border border-gray-200 p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="font-display text-lg font-semibold text-[#111827]">{lead.name}</h3>
          {lead.company && (
            <p className="text-sm text-gray-500 flex items-center gap-1.5 mt-0.5">
              <Building2 className="w-3.5 h-3.5" /> {lead.company}
            </p>
          )}
        </div>
        <span className="font-mono-axis text-xs text-gray-400">
          {new Date(lead.created_at).toLocaleString("pt-BR")}
        </span>
      </div>
      <div className="mt-3 flex flex-wrap gap-4 text-sm">
        <a href={`mailto:${lead.email}`} className="flex items-center gap-1.5 text-[#1E3A8A] hover:underline">
          <Mail className="w-3.5 h-3.5" /> {lead.email}
        </a>
        {lead.phone && (
          <a href={`tel:${lead.phone}`} className="flex items-center gap-1.5 text-[#1E3A8A] hover:underline">
            <Phone className="w-3.5 h-3.5" /> {lead.phone}
          </a>
        )}
      </div>
      <p className="mt-4 text-sm text-gray-700 whitespace-pre-wrap bg-[#F9FAFB] border-l-2 border-[#3B82F6] p-3">
        {lead.message}
      </p>
    </article>
  );
}

function LeadsBoard({ onLogout }) {
  const [leads, setLeads] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/contact", { headers: authHeader() });
      setLeads(data);
    } catch (err) {
      if (err.response?.status === 401) onLogout();
    } finally {
      setLoading(false);
    }
  }, [onLogout]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const t = q.toLowerCase();
    return leads.filter((l) =>
      [l.name, l.company, l.email, l.message].some((v) => (v || "").toLowerCase().includes(t))
    );
  }, [leads, q]);

  return (
    <div className="min-h-screen bg-[#F3F4F6]">
      <header className="bg-[#050B18] text-white">
        <div className="max-w-5xl mx-auto px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Logo variant="light" />
            <span className="font-mono-axis text-xs uppercase tracking-widest text-[#22D3EE]">Leads</span>
          </div>
          <button
            type="button"
            onClick={onLogout}
            data-testid="admin-logout-btn"
            className="flex items-center gap-2 text-sm text-gray-300 hover:text-white"
          >
            <LogOut className="w-4 h-4" /> Sair
          </button>
        </div>
      </header>
      <main className="max-w-5xl mx-auto px-6 py-10">
        <div className="flex flex-wrap items-center gap-4 justify-between mb-6">
          <p data-testid="admin-leads-count" className="font-display text-2xl font-semibold">
            {filtered.length} contato{filtered.length === 1 ? "" : "s"}
          </p>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                data-testid="admin-leads-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Buscar..."
                className="pl-9 pr-3 py-2 border border-gray-300 text-sm bg-white outline-none focus:border-[#3B82F6]"
              />
            </div>
            <button
              type="button"
              onClick={load}
              data-testid="admin-leads-refresh"
              aria-label="Atualizar"
              className="p-2 border border-gray-300 bg-white hover:border-[#3B82F6]"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </button>
          </div>
        </div>
        <div className="space-y-4" data-testid="admin-leads-list">
          {!loading && filtered.length === 0 && (
            <p data-testid="admin-leads-empty" className="text-sm text-gray-500">Nenhum contato encontrado.</p>
          )}
          {filtered.map((l) => (
            <LeadCard key={l.id} lead={l} />
          ))}
        </div>
      </main>
    </div>
  );
}

export default function AdminLeads() {
  const [user, setUser] = useState(null);

  useEffect(() => {
    document.title = "Leads · Áxis Saúde Digital";
    if (!sessionStorage.getItem(TOKEN_KEY)) return setUser(false);
    api
      .get("/auth/me", { headers: authHeader() })
      .then(({ data }) => setUser(data))
      .catch(() => setUser(false));
  }, []);

  const logout = useCallback(() => {
    sessionStorage.removeItem(TOKEN_KEY);
    api.post("/auth/logout", {}, { withCredentials: true }).catch(() => {});
    setUser(false);
  }, []);

  if (user === null) return <div className="min-h-screen bg-[#050B18]" />;
  if (!user) return <LoginForm onLogin={setUser} />;
  return <LeadsBoard onLogout={logout} />;
}
